'use client';

import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

export type ConsentStatus = 'undecided' | 'all' | 'essential' | 'custom';

export interface ConsentPreferences {
  soundCloud: boolean;
  youtube: boolean;
}

export interface ConsentContextValue {
  consent: ConsentStatus;
  preferences: ConsentPreferences;
  isBannerOpen: boolean;
  isSoundCloudPromptOpen: boolean;
  pendingTrackInfo: { url: string; title?: string } | null;
  acceptAll: () => void;
  acceptEssential: () => void;
  savePreferences: (prefs: ConsentPreferences) => void;
  allowSoundCloud: () => void;
  allowYouTube: () => void;
  requestSoundCloudPermission: (
    trackUrl?: string,
    trackTitle?: string,
    onAllowed?: () => void
  ) => boolean;
  confirmSoundCloudPermission: () => void;
  cancelSoundCloudPrompt: () => void;
  openSettings: () => void;
  closeBanner: () => void;
}

const STORAGE_KEY = 'joev_privacy_consent';
const PREFS_STORAGE_KEY = 'joev_privacy_preferences';

const defaultPreferences: ConsentPreferences = {
  soundCloud: false,
  youtube: false,
};

const ConsentContext = createContext<ConsentContextValue | null>(null);

export const ConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [consent, setConsent] = useState<ConsentStatus>('undecided');
  const [preferences, setPreferences] = useState<ConsentPreferences>(defaultPreferences);
  const [isBannerOpen, setIsBannerOpen] = useState(false);
  const [isSoundCloudPromptOpen, setIsSoundCloudPromptOpen] = useState(false);
  const [pendingTrackInfo, setPendingTrackInfo] = useState<{ url: string; title?: string } | null>(
    null
  );
  const pendingCallbackRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    try {
      const savedConsent = localStorage.getItem(STORAGE_KEY) as ConsentStatus | null;
      const savedPrefs = localStorage.getItem(PREFS_STORAGE_KEY);

      if (savedConsent === 'all') {
        setConsent('all');
        setPreferences({ soundCloud: true, youtube: true });
        setIsBannerOpen(false);
      } else if (savedConsent === 'essential') {
        setConsent('essential');
        setPreferences({ soundCloud: false, youtube: false });
        setIsBannerOpen(false);
      } else if (savedConsent === 'custom' && savedPrefs) {
        setConsent('custom');
        try {
          setPreferences(JSON.parse(savedPrefs));
        } catch {
          setPreferences(defaultPreferences);
        }
        setIsBannerOpen(false);
      } else {
        setConsent('undecided');
        setIsBannerOpen(true);
      }
    } catch {
      setIsBannerOpen(true);
    }
  }, []);

  const acceptAll = useCallback(() => {
    const allPrefs = { soundCloud: true, youtube: true };
    setConsent('all');
    setPreferences(allPrefs);
    setIsBannerOpen(false);
    setIsSoundCloudPromptOpen(false);
    try {
      localStorage.setItem(STORAGE_KEY, 'all');
      localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(allPrefs));
    } catch {
      // Ignore storage errors
    }
  }, []);

  const acceptEssential = useCallback(() => {
    const noPrefs = { soundCloud: false, youtube: false };
    setConsent('essential');
    setPreferences(noPrefs);
    setIsBannerOpen(false);
    setIsSoundCloudPromptOpen(false);
    try {
      localStorage.setItem(STORAGE_KEY, 'essential');
      localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(noPrefs));
    } catch {
      // Ignore storage errors
    }
  }, []);

  const savePreferences = useCallback((prefs: ConsentPreferences) => {
    const isAll = prefs.soundCloud && prefs.youtube;
    const isNone = !prefs.soundCloud && !prefs.youtube;
    const status: ConsentStatus = isAll ? 'all' : isNone ? 'essential' : 'custom';

    setConsent(status);
    setPreferences(prefs);
    setIsBannerOpen(false);
    setIsSoundCloudPromptOpen(false);
    try {
      localStorage.setItem(STORAGE_KEY, status);
      localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      // Ignore storage errors
    }
  }, []);

  const allowSoundCloud = useCallback(() => {
    setPreferences((prev) => {
      const updated = { ...prev, soundCloud: true };
      const status: ConsentStatus = updated.youtube ? 'all' : 'custom';
      setConsent(status);
      try {
        localStorage.setItem(STORAGE_KEY, status);
        localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(updated));
      } catch {
        // Ignore storage errors
      }
      return updated;
    });
    setIsSoundCloudPromptOpen(false);
  }, []);

  const allowYouTube = useCallback(() => {
    setPreferences((prev) => {
      const updated = { ...prev, youtube: true };
      const status: ConsentStatus = updated.soundCloud ? 'all' : 'custom';
      setConsent(status);
      try {
        localStorage.setItem(STORAGE_KEY, status);
        localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(updated));
      } catch {
        // Ignore storage errors
      }
      return updated;
    });
  }, []);

  const requestSoundCloudPermission = useCallback(
    (trackUrl?: string, trackTitle?: string, onAllowed?: () => void): boolean => {
      if (preferences.soundCloud) {
        if (onAllowed) onAllowed();
        return true;
      }
      if (trackUrl) {
        setPendingTrackInfo({ url: trackUrl, title: trackTitle });
      }
      pendingCallbackRef.current = onAllowed || null;
      setIsSoundCloudPromptOpen(true);
      return false;
    },
    [preferences.soundCloud]
  );

  const confirmSoundCloudPermission = useCallback(() => {
    allowSoundCloud();
    setIsSoundCloudPromptOpen(false);
    if (pendingCallbackRef.current) {
      pendingCallbackRef.current();
      pendingCallbackRef.current = null;
    }
  }, [allowSoundCloud]);

  const cancelSoundCloudPrompt = useCallback(() => {
    setIsSoundCloudPromptOpen(false);
    setPendingTrackInfo(null);
    pendingCallbackRef.current = null;
  }, []);

  const openSettings = useCallback(() => {
    setIsBannerOpen(true);
  }, []);

  const closeBanner = useCallback(() => {
    setIsBannerOpen(false);
  }, []);

  return (
    <ConsentContext.Provider
      value={{
        consent,
        preferences,
        isBannerOpen,
        isSoundCloudPromptOpen,
        pendingTrackInfo,
        acceptAll,
        acceptEssential,
        savePreferences,
        allowSoundCloud,
        allowYouTube,
        requestSoundCloudPermission,
        confirmSoundCloudPermission,
        cancelSoundCloudPrompt,
        openSettings,
        closeBanner,
      }}
    >
      {children}
    </ConsentContext.Provider>
  );
};

export const useConsent = (): ConsentContextValue => {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    return {
      consent: 'undecided',
      preferences: defaultPreferences,
      isBannerOpen: false,
      isSoundCloudPromptOpen: false,
      pendingTrackInfo: null,
      acceptAll: () => {},
      acceptEssential: () => {},
      savePreferences: () => {},
      allowSoundCloud: () => {},
      allowYouTube: () => {},
      requestSoundCloudPermission: () => true,
      confirmSoundCloudPermission: () => {},
      cancelSoundCloudPrompt: () => {},
      openSettings: () => {},
      closeBanner: () => {},
    };
  }
  return ctx;
};

export default ConsentContext;
